import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { createHook, createStore } from 'react-sweet-state';
import { useRovoEntitlement } from '@atlassian/conversation-assistant-entitlement';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation';
import { createHookWithAssistanceService, exponentialRetry, isRateLimitedError, useMutation, useQuery } from '@atlassian/conversation-assistant-service';
import { getSuggestedOotbAgents } from '../../utils/agents';
import { useDeleteAgent } from '../agent-delete';
import { useAgentFavourites } from '../agent-favourite';
import { getOverridenAgents, useAgentOverrider } from '../agent-overrider';
import { isHipaaError } from './constants';
import messages from './messages';
var categoriseAgents = function categoriseAgents(agents) {
  return agents.reduce(function (categorisedAgents, agent) {
    if (agent.creator_type === 'SYSTEM') {
      categorisedAgents.system.push(agent);
    } else if (agent.visibility === 'PUBLIC') {
      categorisedAgents.public.push(agent);
    } else if (agent.visibility === 'PRIVATE') {
      categorisedAgents.private.push(agent);
    } else if (!agent.visibility && agent.creator_type === 'CUSTOMER') {
      categorisedAgents.private.push(agent);
    }
    return categorisedAgents;
  }, {
    public: [],
    private: [],
    system: []
  });
};
var agentsStore = createStore({
  initialState: {
    availableAgents: [],
    categorisedAgents: {
      private: [],
      public: [],
      system: []
    },
    agentsInitialized: false,
    agentsError: undefined
  },
  name: 'AgentsStore',
  actions: {
    setAgents: function setAgents(availableAgents) {
      return function (_ref) {
        var setState = _ref.setState;
        setState({
          availableAgents: availableAgents,
          categorisedAgents: categoriseAgents(availableAgents)
        });
      };
    },
    setAgentsInitialized: function setAgentsInitialized() {
      return function (_ref2) {
        var setState = _ref2.setState;
        setState({
          agentsInitialized: true
        });
      };
    },
    setAgentsError: function setAgentsError(agentsError) {
      return function (_ref3) {
        var setState = _ref3.setState;
        setState({
          agentsError: agentsError
        });
      };
    }
  }
});
var useAgentsStore = createHook(agentsStore);
export var useAgentsControllerInternal = createHookWithAssistanceService(function (_ref4) {
  var onError = _ref4.onError,
    _ref4$assistanceServi = _ref4.assistanceServiceInjectedParams,
    assistanceService = _ref4$assistanceServi.assistanceService,
    experienceTracker = _ref4$assistanceServi.experienceTracker,
    experienceId = _ref4$assistanceServi.experienceId,
    product = _ref4$assistanceServi.product,
    siteId = _ref4$assistanceServi.siteId,
    touchpointSource = _ref4$assistanceServi.touchpointSource;
  var _useRovoEntitlement = useRovoEntitlement(),
    isRovoEnabled = _useRovoEntitlement.isRovoEnabled;
  var _useAgentOverrider = useAgentOverrider(),
    agentIdFiltersOverride = _useAgentOverrider.agentIdFiltersOverride,
    agentDefaultIdOverride = _useAgentOverrider.agentDefaultIdOverride;
  var _useAgentsStore = useAgentsStore(),
    _useAgentsStore2 = _slicedToArray(_useAgentsStore, 2),
    _useAgentsStore2$ = _useAgentsStore2[0],
    availableAgents = _useAgentsStore2$.availableAgents,
    categorisedAgents = _useAgentsStore2$.categorisedAgents,
    agentsInitialized = _useAgentsStore2$.agentsInitialized,
    agentsError = _useAgentsStore2$.agentsError,
    _useAgentsStore2$2 = _useAgentsStore2[1],
    setAgents = _useAgentsStore2$2.setAgents,
    setAgentsInitialized = _useAgentsStore2$2.setAgentsInitialized,
    setAgentsError = _useAgentsStore2$2.setAgentsError;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAgentFavourites = useAgentFavourites({
      assistanceServiceParams: {
        experienceId: experienceId,
        product: product,
        siteId: siteId,
        touchpointSource: touchpointSource
      }
    }),
    favouriteAgent = _useAgentFavourites.favouriteAgent,
    unfavouriteAgent = _useAgentFavourites.unfavouriteAgent;
  var favouritedAgents = availableAgents.filter(function (agent) {
    return agent.favourite;
  });
  var defaultAgent = useMemo(function () {
    return availableAgents.find(function (agent) {
      return agent.is_default;
    });
  }, [availableAgents]);
  var nonDefaultAgents = useMemo(function () {
    return availableAgents.filter(function (agent) {
      return !agent.is_default;
    });
  }, [availableAgents]);
  var nonDefaultFavouriteAgents = useMemo(function () {
    return nonDefaultAgents.filter(function (agent) {
      return !agent.favourite;
    });
  }, [nonDefaultAgents]);
  var fetchAndStoreAvailableAgents = useQuery({
    queryKey: ['fetchAgents'],
    queryFn: function () {
      var _queryFn = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var agents, transformedAgents, overriddenAgents;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              experienceTracker.start({
                id: ExperienceName.FETCH_AGENTS,
                name: ExperienceName.FETCH_AGENTS
              });
              _context.next = 4;
              return exponentialRetry({
                fn: function fn() {
                  return assistanceService.getAgents();
                },
                errorFilter: isRateLimitedError
              });
            case 4:
              agents = _context.sent;
              transformedAgents = agents.map(function (agent) {
                // Rename default agent to Chat, which is displayed in the UI
                if (agent.is_default) {
                  agent.name = formatMessage(messages.defaultAgentName);
                  agent.description = formatMessage(messages.defaultAgentDescription);
                }
                return agent;
              });
              overriddenAgents = getOverridenAgents(transformedAgents, agentIdFiltersOverride, agentDefaultIdOverride);
              setAgents(overriddenAgents);
              experienceTracker.succeed({
                name: ExperienceName.FETCH_AGENTS
              });
              setAgentsInitialized();
              _context.next = 20;
              break;
            case 12:
              _context.prev = 12;
              _context.t0 = _context["catch"](0);
              if (!isHipaaError(_context.t0)) {
                _context.next = 17;
                break;
              }
              experienceTracker.succeed({
                name: ExperienceName.FETCH_AGENTS
              });
              return _context.abrupt("return");
            case 17:
              if (isRateLimitedError(_context.t0)) {
                setAgentsError(_context.t0);
              }
              experienceTracker.fail({
                name: ExperienceName.FETCH_AGENTS,
                error: _context.t0
              });
              throw _context.t0;
            case 20:
            case "end":
              return _context.stop();
          }
        }, _callee, null, [[0, 12]]);
      }));
      function queryFn() {
        return _queryFn.apply(this, arguments);
      }
      return queryFn;
    }(),
    onError: onError,
    enabled: isRovoEnabled,
    refetchOnWindowFocus: true
  });
  var _useDeleteAgent = useDeleteAgent({
      onSuccess: function onSuccess() {
        fetchAndStoreAvailableAgents.refetch();
      },
      assistanceServiceParams: {
        experienceId: experienceId,
        product: product,
        siteId: siteId,
        touchpointSource: touchpointSource
      },
      onInit: function onInit(id) {
        setAgents(availableAgents.filter(function (agent) {
          return agent.id !== id;
        }));
      },
      onError: function onError() {
        setAgents(availableAgents);
      }
    }),
    deleteAgent = _useDeleteAgent.deleteAgent;
  var setFavouriteAgent = useCallback( /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(id, isFavourite) {
      var updatedAgent, setUpdatedAgent, errorCallback;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            updatedAgent = availableAgents.find(function (a) {
              return a.id === id;
            });
            setUpdatedAgent = function setUpdatedAgent(shouldBeFavourite) {
              if (updatedAgent) {
                updatedAgent.favourite = shouldBeFavourite;
                setAgents(availableAgents.map(function (a) {
                  // If the current agent's id matches the new agent's id, return the new agent.
                  // Otherwise, return the current agent.
                  return a.id === updatedAgent.id ? updatedAgent : a;
                }));
              }
            }; //Optimistically update favourite value before we mutate
            setUpdatedAgent(isFavourite);

            //Pass callback to roll back optimistic update if mutation fails
            errorCallback = {
              onError: function onError() {
                setUpdatedAgent(!isFavourite);
              }
            };
            if (!isFavourite) {
              _context2.next = 10;
              break;
            }
            _context2.next = 7;
            return favouriteAgent.mutate(id, errorCallback);
          case 7:
            return _context2.abrupt("return", _context2.sent);
          case 10:
            _context2.next = 12;
            return unfavouriteAgent.mutate(id, errorCallback);
          case 12:
            return _context2.abrupt("return", _context2.sent);
          case 13:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return function (_x, _x2) {
      return _ref5.apply(this, arguments);
    };
  }(), [availableAgents, setAgents, favouriteAgent, unfavouriteAgent]);
  /** This updates the agent on the client side only
   * Useful for updating the agent after a successful mutation
   */
  var updateAgentLocally = useCallback(function (agentId, updatedAgent) {
    var updatedAgents = availableAgents.map(function (agent) {
      if (agent.id === agentId) {
        return _objectSpread(_objectSpread({}, agent), updatedAgent);
      }
      return agent;
    });
    setAgents(updatedAgents);
  }, [availableAgents, setAgents]);
  return {
    availableAgents: availableAgents,
    favouritedAgents: favouritedAgents,
    categorisedAgents: categorisedAgents,
    fetchAndStoreAvailableAgents: fetchAndStoreAvailableAgents,
    deleteAgent: deleteAgent,
    setFavouriteAgent: setFavouriteAgent,
    defaultAgent: defaultAgent,
    agentsInitialized: agentsInitialized,
    nonDefaultAgents: nonDefaultAgents,
    nonDefaultFavouriteAgents: nonDefaultFavouriteAgents,
    updateAgentLocally: updateAgentLocally,
    agentsError: agentsError
  };
});
var noopAsync = /*#__PURE__*/function () {
  var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
        case "end":
          return _context3.stop();
      }
    }, _callee3);
  }));
  return function noopAsync() {
    return _ref6.apply(this, arguments);
  };
}();
var noopFn = function noopFn() {
  return undefined;
};
var noopArray = [];
var noopCategorisedAgent = {
  public: [],
  private: [],
  system: []
};
export var noopAgent = {
  id: 'NOOP',
  name: 'NOOP',
  named_id: 'NOOP',
  user_defined_conversation_starters: [],
  description: '',
  available_plugins: {},
  actor_type: 'AGENT',
  creator_type: 'SYSTEM',
  visibility: 'PRIVATE',
  is_default: false,
  favourite: false,
  deactivated: false
};
export var useAgentsController = function useAgentsController(param) {
  var _useRovoEntitlement2 = useRovoEntitlement(),
    isRovoEnabled = _useRovoEntitlement2.isRovoEnabled;
  var controllerResult = useAgentsControllerInternal(param);
  var noopUseQuery = useQuery({
    queryKey: ['noop'],
    queryFn: noopAsync,
    enabled: false
  });
  var noopUseMutation = useMutation({
    mutationFn: function () {
      var _mutationFn = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) switch (_context4.prev = _context4.next) {
            case 0:
              return _context4.abrupt("return", noopAgent);
            case 1:
            case "end":
              return _context4.stop();
          }
        }, _callee4);
      }));
      function mutationFn() {
        return _mutationFn.apply(this, arguments);
      }
      return mutationFn;
    }(),
    onSuccess: noopAsync,
    onError: noopAsync
  });
  if (!isRovoEnabled) {
    return {
      availableAgents: noopArray,
      favouritedAgents: noopArray,
      categorisedAgents: noopCategorisedAgent,
      defaultAgent: undefined,
      agentsInitialized: false,
      fetchAndStoreAvailableAgents: noopUseQuery,
      deleteAgent: noopUseMutation,
      setFavouriteAgent: noopUseMutation,
      nonDefaultAgents: noopArray,
      nonDefaultFavouriteAgents: noopArray,
      updateAgentLocally: noopFn,
      agentsError: undefined
    };
  }
  return controllerResult;
};

//Use useAgentsList in contexts in which we want to display at least three default favourited agents, such as in the editor
export var useAgentsList = createHookWithAssistanceService(function (_ref7) {
  var _ref7$assistanceServi = _ref7.assistanceServiceInjectedParams,
    product = _ref7$assistanceServi.product,
    experienceId = _ref7$assistanceServi.experienceId,
    headers = _ref7$assistanceServi.headers,
    touchpointSource = _ref7$assistanceServi.touchpointSource,
    siteId = _ref7$assistanceServi.siteId,
    filter = _ref7.filter;
  var _useAgentsController = useAgentsController({
      assistanceServiceParams: {
        product: product,
        experienceId: experienceId,
        headers: headers,
        touchpointSource: touchpointSource,
        siteId: siteId
      }
    }),
    availableAgents = _useAgentsController.availableAgents,
    favouritedAgents = _useAgentsController.favouritedAgents;
  var defaultAgents = getSuggestedOotbAgents({
    availableAgents: availableAgents,
    product: product
  });
  var defaultAgentsOutsideUserFavourites = defaultAgents.filter(function (defaultAgent) {
    return !favouritedAgents.find(function (favAgent) {
      return favAgent.id === defaultAgent.id;
    });
  });
  var favouritedAgentsWithDefault = [].concat(_toConsumableArray(favouritedAgents), _toConsumableArray(defaultAgentsOutsideUserFavourites.slice(0, Math.max(3 - favouritedAgents.length, 0))));
  return filter === 'favourites' ? favouritedAgentsWithDefault : availableAgents;
});